import { use } from "react";
import type { ReactNode } from "react";
import { liveWorkEntryLabel } from "./MessagesTimeline.labels";
import { workEntryDisplayIndicatesToolFailure } from "../../session-logic";
import { summarizeToolGroup } from "../../workLogToolGroups";
import {
  workEntryIsActiveTurnActivity,
  workEntryIsVisibleInGroup,
} from "./MessagesTimeline.activityGroups";
import { omitSupersededLifecycleMarkers } from "../../workLogToolGroups";
import type { MessagesTimelineRow } from "./MessagesTimeline.rowTypes";
import { TimelineRowCtx } from "./TimelineRowContext";
import { LiveActivityRow } from "./TimelineLiveActivityRow";
import { WorkGroupSection } from "./TimelineLiveRows";
import { workEntryIconName } from "./TimelineWorkEntryIcons";
import { ReasoningTraceBlock } from "./TimelineAssistantRows";

/**
 * The shared activity row (ticket 3525, upstream `ActivityGroupTimelineRow`):
 * interleaved thoughts and tool calls read as one live row that expands into
 * ordered traces and work.
 */
// fallow-ignore-next-line complexity -- port of upstream MessagesTimeline.tsx at pin e3e7cc3f; refactoring would break the provenance pin (ticket 3525)
export function ActivityGroupTimelineRow({
  row,
}: {
  row: Extract<MessagesTimelineRow, { kind: "activity-group" }>;
}) {
  const ctx = use(TimelineRowCtx);
  const work = omitSupersededLifecycleMarkers(
    row.entries.flatMap((entry) =>
      entry.kind === "work" && workEntryIsVisibleInGroup(entry.entry, row.active)
        ? [entry.entry]
        : [],
    ),
    (entry) => entry,
  );
  const thoughtCount = row.entries.filter((entry) => entry.kind === "message").length;
  const lastThoughtIndex = row.entries.findLastIndex((entry) => entry.kind === "message");
  const trailingWork = omitSupersededLifecycleMarkers(
    row.entries
      .slice(lastThoughtIndex + 1)
      .flatMap((entry) =>
        entry.kind === "work" && workEntryIsVisibleInGroup(entry.entry, row.active)
          ? [entry.entry]
          : [],
      ),
    (entry) => entry,
  );
  const liveWork = trailingWork.findLast(workEntryIsActiveTurnActivity) ?? trailingWork.at(-1);
  const thinking = row.active && liveWork === undefined;
  const iconWork = row.active ? liveWork : work.at(-1);
  const failed = iconWork !== undefined && workEntryDisplayIndicatesToolFailure(iconWork);
  const label = row.active
    ? liveWork
      ? liveWorkEntryLabel(liveWork, ctx.workspaceRoot, true)
      : "Thinking"
    : work.length > 0
      ? summarizeToolGroup(work)
      : `Thought${thoughtCount > 1 ? ` (×${thoughtCount})` : ""}`;
  const details: ReactNode[] = [];
  if (row.expanded) {
    for (let index = 0; index < row.entries.length; index += 1) {
      const entry = row.entries[index]!;
      if (entry.kind === "work") {
        const entries = [entry.entry];
        while (row.entries[index + 1]?.kind === "work") {
          const next = row.entries[++index]!;
          if (next.kind === "work") entries.push(next.entry);
        }
        details.push(
          <WorkGroupSection
            key={entry.id}
            anchorKey={entry.id}
            groupedEntries={omitSupersededLifecycleMarkers(entries, (entry) => entry)}
            isExpandedToolGroup
          />,
        );
      } else {
        const messages = [entry.message];
        while (row.entries[index + 1]?.kind === "message") {
          const next = row.entries[++index]!;
          if (next.kind === "message") messages.push(next.message);
        }
        details.push(
          <ReasoningTraceBlock
            key={entry.id}
            messages={messages}
            live={row.active && index === row.entries.length - 1}
            showHeader={work.length > 0}
          />,
        );
      }
    }
  }
  return (
    <div>
      <button
        type="button"
        className="group/live-work flex min-h-6 w-full max-w-full cursor-pointer items-center rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/70"
        aria-label={failed ? `${label}, tool call failed` : undefined}
        aria-expanded={row.expanded}
        onClick={() => ctx.onToggleWorkGroup(row.groupId)}
      >
        <LiveActivityRow
          label={label}
          iconName={iconWork ? workEntryIconName(iconWork) : "brain"}
          toolIcon={iconWork?.toolIcon ?? iconWork?.toolSource?.icon}
          failed={failed}
          active={row.active}
          shimmer={thinking}
        />
      </button>
      {row.expanded ? <div className="mt-2">{details}</div> : null}
    </div>
  );
}
